/**
 * AmenityPanel — choose which kinds of place appear on the map.
 *
 * The points service speaks OSM's taxonomy: 128 amenity values, from `fuel`
 * to `bureau_de_change` to `hunting_stand`. A flat list of 128 is not a
 * choice anyone can make, so the panel has two views over one selection:
 *
 *   'purpose'  nine cards naming an everyday need — "Getting around",
 *              "Eat & drink". Tapping one turns that whole group on or off.
 *              This is the front door, because nobody opens a map thinking
 *              "amenity=fuel"; they think "I need petrol".
 *   'kinds'    the same groups opened up: a row per purpose with a switch,
 *              expanding to the individual kinds and their counts. Reached
 *              from "Choose kinds myself".
 *
 * Both views share the Show all / Hide all control and the running count.
 *
 * Usage:
 *   openAmenityPanel({ amenities, active, onChange });
 *
 * @module components/AmenityPanel
 */

import { injectGlassStyles } from './glassForm.js';

/** Escape text for interpolation into the panel's markup. */
function escapeHtml(v) {
  return String(v ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

/**
 * Purposes, in the order they appear on the panel.
 *
 * The points service speaks OSM's taxonomy — 128 amenity values, from `fuel`
 * to `bureau_de_change` to `hunting_stand`. Nobody opens the map thinking in
 * those terms; they think "I need petrol". Each purpose therefore gathers the
 * amenity values that answer one everyday need.
 *
 * `keys` may name amenities that do not exist in the data — the lists are
 * written against OSM's vocabulary rather than today's extract, so the app
 * keeps working when the upstream data grows. Anything not claimed by a
 * purpose falls into "Everything else" automatically (see poiCategoryList).
 */
const POI_CATEGORIES = [
  {
    key: 'transport', emoji: '🚌', title: 'Getting around',
    hint: 'Fuel · Bus · Taxi · Parking',
    keys: ['fuel', 'bus_station', 'taxi', 'parking', 'parking_entrance', 'parking_space',
           'ferry_terminal', 'car_wash', 'car_rental', 'car_repair', 'driving_school',
           'bicycle_parking', 'bicycle_rental', 'bicycle_repair_station', 'charging_station',
           'motorcycle_parking', 'boat_rental', 'bus_stop', 'compressed_air',
           'weighbridge', 'vehicle_inspection'],
  },
  {
    key: 'food', emoji: '🍽️', title: 'Eat & drink',
    hint: 'Restaurant · Bar · Market',
    keys: ['restaurant', 'fast_food', 'cafe', 'bar', 'pub', 'food_court', 'ice_cream',
           'nightclub', 'biergarten', 'marketplace', 'bbq'],
  },
  {
    key: 'health', emoji: '🩺', title: 'Health',
    hint: 'Clinic · Pharmacy · Hospital',
    keys: ['pharmacy', 'hospital', 'clinic', 'doctors', 'health_post', 'dentist',
           'veterinary', 'mortuary', 'healthcare', 'laboratory', 'chemist',
           'nursing_home', 'blood_donation'],
  },
  {
    key: 'money', emoji: '🏦', title: 'Money',
    hint: 'Bank · ATM · Mobile money',
    keys: ['bank', 'atm', 'bureau_de_change', 'mobile_money_agent', 'money_transfer',
           'payment_centre', 'payment_terminal', 'microfinance'],
  },
  {
    key: 'water', emoji: '🚰', title: 'Water & toilets',
    hint: 'Water point · Toilets',
    keys: ['toilets', 'water_point', 'drinking_water', 'shower', 'fountain',
           'watering_place', 'public_bath'],
  },
  {
    key: 'civic', emoji: '🏫', title: 'School & civic',
    hint: 'School · Police · Post',
    keys: ['school', 'kindergarten', 'college', 'university', 'prep_school', 'library',
           'research_institute', 'language_school', 'music_school', 'training', 'childcare',
           'police', 'fire_station', 'townhall', 'courthouse', 'post_office',
           'community_centre', 'social_facility', 'social_centre', 'embassy', 'prison',
           'polling_station', 'public_building', 'ranger_station', 'customs',
           'register_office', 'conference_centre', 'coworking_space'],
  },
  {
    key: 'waste', emoji: '🗑️', title: 'Waste & recycling',
    hint: 'Bins · Dump sites · Recycling',
    keys: ['waste_basket', 'waste_disposal', 'waste_dump_site', 'recycling',
           'waste_transfer_station', 'sanitary_dump_station'],
  },
  {
    key: 'leisure', emoji: '⛪', title: 'Worship & leisure',
    hint: 'Church · Cinema · Sport',
    keys: ['place_of_worship', 'cinema', 'theatre', 'arts_centre', 'events_venue',
           'casino', 'gambling', 'studio', 'grave_yard', 'swimming_pool', 'sports_centre',
           'fitness_centre', 'dojo', 'stage', 'music_venue'],
  },
];

// Everything not claimed above — waste handling, small services and the long
// tail of rare types — is gathered here rather than left unreachable.
const POI_OTHER_CATEGORY = {
  key: 'other', emoji: '🛠️', title: 'Everything else',
  hint: 'Waste · Shops · Rare kinds',
  keys: null,   // computed: whatever no other purpose claimed
};

/**
 * Purposes filled in with the amenities that actually exist in the data.
 * Empty purposes are dropped, so the panel never offers a card that would
 * show nothing. Anything no purpose claims lands in "Everything else".
 *
 * @param {Array} all      [{ key, emoji, label, count }]
 * @param {Set}   selected amenity keys currently shown
 * @returns {Array<{key, emoji, title, hint, items, typeCount, placeCount, state}>}
 *          state is 'on' (all its kinds shown), 'partial', or 'off'.
 */
export function buildCategories(all, selected) {
  const byKey = new Map(all.map((a) => [a.key, a]));
  const claimed = new Set();

  const build = (cat, items) => {
    const on = items.filter((i) => selected.has(i.key)).length;
    return {
      ...cat,
      items,
      typeCount: items.length,
      placeCount: items.reduce((n, i) => n + i.count, 0),
      state: on === 0 ? 'off' : (on === items.length ? 'on' : 'partial'),
    };
  };

  const cats = POI_CATEGORIES.map((cat) => {
    const items = cat.keys
      .filter((k) => byKey.has(k))
      .map((k) => { claimed.add(k); return byKey.get(k); })
      .sort((a, b) => b.count - a.count);
    return build(cat, items);
  }).filter((c) => c.typeCount > 0);

  const rest = all.filter((a) => !claimed.has(a.key));
  if (rest.length) cats.push(build(POI_OTHER_CATEGORY, rest));
  return cats;
}

/**
 * Open the panel.
 *
 * @param {object} opts
 * @param {Array}  opts.amenities  [{ key, emoji, label, count }] present in the data
 * @param {Set}    opts.active     amenity keys currently shown (not mutated)
 * @param {(next:Set)=>void} opts.onChange  called with a new Set on every change
 */
export function openAmenityPanel({ amenities = [], active = new Set(), onChange } = {}) {
  injectGlassStyles();
  _ensurePoiPanelStyles();

  let selected = new Set(active);
  const commit = () => onChange?.(new Set(selected));

  const FALLBACK_ICON = `${import.meta.env.BASE_URL}img/bw-16x16.png`;
  let view = 'purpose';
  const expanded = new Set();   // category keys opened in the 'kinds' view

  const overlay = document.createElement('div');
  overlay.className = 'glass-overlay';
  overlay.innerHTML = `
    <div class="glass-card" role="dialog" aria-modal="true">
      <div class="glass-grabber" aria-hidden="true"></div>
      <header class="glass-header">
        <div class="glass-icon" aria-hidden="true"><i class="bi bi-shop"></i></div>
        <div>
          <h2 class="glass-title" data-title>What are you looking for?</h2>
          <p class="glass-subtitle" data-subtitle>Pick a purpose — or choose kinds yourself</p>
        </div>
      </header>
      <div class="glass-body" data-body></div>
      <footer class="glass-footer">
        <button type="button" class="glass-btn glass-btn-ghost" data-close>Close</button>
      </footer>
    </div>`;
  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('glass-open'));

  const body = overlay.querySelector('[data-body]');
  const titleEl = overlay.querySelector('[data-title]');
  const subtitleEl = overlay.querySelector('[data-subtitle]');

  const icon = (a) => a.emoji
    ? `<span class="poi-chip-icon">${a.emoji}</span>`
    : `<img class="poi-chip-icon poi-chip-img" src="${FALLBACK_ICON}" alt="">`;

  // ── shared toolbar: how much is on, plus Show all / Hide all ──
  const toolbarHtml = (cats) => {
    const totalTypes = cats.reduce((n, c) => n + c.typeCount, 0);
    const shownTypes = cats.reduce(
      (n, c) => n + c.items.filter((i) => selected.has(i.key)).length, 0);
    const shownPlaces = cats.reduce(
      (n, c) => n + c.items.filter((i) => selected.has(i.key))
        .reduce((m, i) => m + i.count, 0), 0);
    const label = shownTypes === 0
      ? 'Nothing shown'
      : `${shownTypes} of ${totalTypes} · ${shownPlaces.toLocaleString()} places`;
    const allOn = shownTypes === totalTypes;
    return `
      <div class="poi-panel-toolbar">
        <span class="poi-panel-count">${escapeHtml(label)}</span>
        <button type="button" class="poi-panel-toggle-all" data-toggle-all
                data-action="${allOn ? 'hide' : 'show'}">${allOn ? 'Hide all' : 'Show all'}</button>
      </div>`;
  };

  // ── view 1: purpose cards ──
  const purposeHtml = (cats) => `
    ${toolbarHtml(cats)}
    <div class="poi-purpose-grid">
      ${cats.map((c) => `
        <button type="button" class="poi-purpose-card poi-state-${c.state}" data-category="${escapeHtml(c.key)}">
          <span class="poi-purpose-emoji">${c.emoji}</span>
          <span class="poi-purpose-title">${escapeHtml(c.title)}</span>
          <span class="poi-purpose-hint">${escapeHtml(c.hint)}</span>
          <span class="poi-purpose-mark" aria-hidden="true">${
            c.state === 'on' ? '<i class="bi bi-check-lg"></i>'
            : c.state === 'partial' ? '<i class="bi bi-dash-lg"></i>' : ''}</span>
        </button>`).join('')}
    </div>
    <button type="button" class="poi-kinds-link" data-goto-kinds>
      <i class="bi bi-list-ul"></i>
      <span>Choose kinds myself (${cats.reduce((n, c) => n + c.typeCount, 0)})</span>
      <i class="bi bi-chevron-right"></i>
    </button>`;

  // ── view 2: the same groups, opened up ──
  const kindsHtml = (cats) => `
    ${toolbarHtml(cats)}
    <div class="poi-group-list">
      ${cats.map((c) => {
        const open = expanded.has(c.key);
        return `
        <div class="poi-group${open ? ' open' : ''}">
          <div class="poi-group-head" data-expand="${escapeHtml(c.key)}"
               role="button" tabindex="0" aria-expanded="${open}">
            <span class="poi-group-emoji">${c.emoji}</span>
            <span class="poi-group-text">
              <span class="poi-group-title">${escapeHtml(c.title)}</span>
              <span class="poi-group-meta">${c.typeCount} kinds · ${c.placeCount.toLocaleString()} places</span>
            </span>
            <button type="button" class="poi-switch poi-switch-${c.state}"
                    data-category="${escapeHtml(c.key)}"
                    aria-label="Show all ${escapeHtml(c.title)}"
                    aria-pressed="${c.state === 'on'}"><span></span></button>
            <i class="bi bi-chevron-${open ? 'down' : 'right'} poi-group-chevron"></i>
          </div>
          ${open ? `<div class="poi-panel-chips">${c.items.map((a) => `
            <button type="button" class="poi-panel-chip${selected.has(a.key) ? ' on' : ''}"
                    data-amenity="${escapeHtml(a.key)}">
              ${icon(a)} ${escapeHtml(a.label)}
              <span style="opacity:0.6;font-weight:600;">${a.count.toLocaleString()}</span>
            </button>`).join('')}</div>` : ''}
        </div>`;
      }).join('')}
    </div>`;

  function render() {
    const cats = buildCategories(amenities, selected);
    const onKinds = view === 'kinds';
    titleEl.textContent = onKinds ? 'Kinds of place' : 'What are you looking for?';
    subtitleEl.textContent = onKinds
      ? 'Tap a group to open it, or use its switch'
      : 'Pick a purpose — or choose kinds yourself';
    body.innerHTML = onKinds
      ? `<button type="button" class="poi-back" data-back>
           <i class="bi bi-chevron-left"></i> Purposes
         </button>${kindsHtml(cats)}`
      : purposeHtml(cats);
  }
  render();

  /** Turn a whole purpose on or off. Partial counts as off, so a tap fills it. */
  function toggleCategory(key) {
    const cat = buildCategories(amenities, selected).find((c) => c.key === key);
    if (!cat) return;
    if (cat.state === 'on') cat.items.forEach((i) => selected.delete(i.key));
    else cat.items.forEach((i) => selected.add(i.key));
    commit();
    render();
  }

  const close = () => {
    overlay.classList.remove('glass-open');
    setTimeout(() => overlay.remove(), 350);
  };

  overlay.addEventListener('click', (e) => {
    const card = e.target.closest('[data-category]');
    if (card) { toggleCategory(card.dataset.category); return; }

    const chip = e.target.closest('.poi-panel-chip');
    if (chip) {
      const a = chip.dataset.amenity;
      // Deselecting everything is allowed — it clears the map of clusters.
      if (selected.has(a)) selected.delete(a);
      else selected.add(a);
      commit();
      render();
      return;
    }

    const head = e.target.closest('[data-expand]');
    if (head) {
      const k = head.dataset.expand;
      expanded.has(k) ? expanded.delete(k) : expanded.add(k);
      render();
      return;
    }

    const toggleAll = e.target.closest('[data-toggle-all]');
    if (toggleAll) {
      if (toggleAll.dataset.action === 'hide') {
        selected.clear();
      } else {
        buildCategories(amenities, selected).forEach((c) => c.items.forEach((i) => selected.add(i.key)));
      }
      commit();
      render();
      return;
    }

    if (e.target.closest('[data-goto-kinds]')) { view = 'kinds'; render(); return; }
    if (e.target.closest('[data-back]')) { view = 'purpose'; expanded.clear(); render(); return; }
    if (e.target.closest('[data-close]') || e.target === overlay) close();
  });

  // Group headers are keyboard-operable as well as tappable.
  overlay.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const head = e.target.closest?.('[data-expand]');
    if (!head) return;
    e.preventDefault();
    const k = head.dataset.expand;
    expanded.has(k) ? expanded.delete(k) : expanded.add(k);
    render();
  });
}

let _poiPanelStyled = false;
function _ensurePoiPanelStyles() {
  if (_poiPanelStyled) return;
  _poiPanelStyled = true;
  const css = `
    .poi-panel-toolbar {
      display:flex; align-items:center; justify-content:space-between;
      margin-bottom:12px; gap:8px; flex-wrap:wrap;
    }
    .poi-panel-count {
      font-size:0.84rem; font-weight:600; color:#5a4a5a;
      flex:1 1 auto; min-width:0;
    }
    .poi-panel-toggle-all {
      flex:0 0 auto; padding:5px 13px; border-radius:999px; font-size:0.82rem; font-weight:700;
      border:1px solid rgba(139,0,139,0.35); background:rgba(255,255,255,0.6);
      color:#8B008B; cursor:pointer; -webkit-tap-highlight-color:transparent;
      transition:background 0.12s; white-space:nowrap;
    }
    .poi-panel-toggle-all:active { background:rgba(139,0,139,0.12); }

    /* ── purpose cards ── */
    .poi-purpose-grid, .poi-group-list, .poi-panel-chips { max-width:100%; }
    .poi-purpose-grid {
      display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:10px;
      max-height:52vh; overflow-y:auto; overscroll-behavior:contain; padding:2px;
    }
    .poi-purpose-card {
      position:relative; display:flex; flex-direction:column; align-items:flex-start;
      gap:2px; padding:13px 14px 14px; border-radius:18px; text-align:left;
      background:rgba(255,255,255,0.85); border:1px solid rgba(0,0,0,0.06);
      cursor:pointer; -webkit-tap-highlight-color:transparent;
      transition:background 0.15s, transform 0.1s, box-shadow 0.15s;
      box-shadow:0 2px 6px rgba(74,16,80,0.08);
    }
    .poi-purpose-card:active { transform:scale(0.98); }
    .poi-purpose-emoji { font-size:1.5rem; line-height:1.5rem; margin-bottom:4px; }
    .poi-purpose-card > span { max-width:100%; }
    .poi-purpose-title { font-weight:700; font-size:0.94rem; color:#1c1020;
      overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .poi-purpose-hint { font-size:0.72rem; color:rgba(40,20,45,0.62); line-height:1.3;
      overflow:hidden; text-overflow:ellipsis; }
    .poi-purpose-card.poi-state-on,
    .poi-purpose-card.poi-state-partial {
      background:linear-gradient(150deg,#8B008B,#a8259c); border-color:transparent;
      box-shadow:0 3px 12px rgba(139,0,139,0.32);
    }
    .poi-state-on .poi-purpose-title, .poi-state-partial .poi-purpose-title { color:#fff; }
    .poi-state-on .poi-purpose-hint, .poi-state-partial .poi-purpose-hint { color:rgba(255,255,255,0.82); }
    .poi-purpose-mark {
      position:absolute; top:10px; right:10px; width:21px; height:21px; border-radius:50%;
      display:flex; align-items:center; justify-content:center;
      font-size:0.72rem; color:#5a4a00; background:#FFE96A;
    }
    .poi-state-off .poi-purpose-mark { display:none; }

    /* ── link through to the full list ── */
    .poi-kinds-link {
      display:flex; align-items:center; gap:10px; width:100%; margin-top:14px;
      padding:13px 16px; border-radius:999px; font-size:0.92rem; font-weight:700;
      border:2px solid #556B2F; background:rgba(255,255,255,0.7); color:#556B2F;
      cursor:pointer; -webkit-tap-highlight-color:transparent;
    }
    .poi-kinds-link span { flex:1 1 auto; text-align:left; }
    .poi-kinds-link:active { background:rgba(85,107,47,0.1); }

    .poi-back {
      display:inline-flex; align-items:center; gap:4px; margin-bottom:12px;
      padding:5px 12px 5px 8px; border-radius:999px; font-size:0.82rem; font-weight:700;
      border:1px solid rgba(0,0,0,0.1); background:rgba(255,255,255,0.7);
      color:#5a4a60; cursor:pointer; -webkit-tap-highlight-color:transparent;
    }

    /* ── grouped kinds ── */
    .poi-group-list {
      display:flex; flex-direction:column; gap:9px;
      max-height:54vh; overflow-y:auto; overscroll-behavior:contain; padding:2px;
    }
    .poi-group { border-radius:14px; background:rgba(255,255,255,0.72);
      border:1px solid rgba(0,0,0,0.07); }
    .poi-group.open { border-color:rgba(139,0,139,0.3); background:rgba(255,255,255,0.9); }
    .poi-group-head {
      display:flex; align-items:center; gap:11px; padding:11px 13px; cursor:pointer;
      -webkit-tap-highlight-color:transparent;
    }
    .poi-group-emoji { font-size:1.15rem; flex:0 0 auto; }
    .poi-group-text { display:flex; flex-direction:column; flex:1 1 auto; min-width:0; }
    .poi-group-title { font-weight:700; font-size:0.9rem; color:#1c1020;
      overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .poi-group-meta { font-size:0.73rem; color:rgba(40,20,45,0.6);
      overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .poi-group-chevron { color:rgba(40,20,45,0.35); font-size:0.8rem; flex:0 0 auto; }

    /* switch: on / partial / off */
    .poi-switch {
      flex:0 0 auto; width:42px; height:24px; border-radius:12px; border:none;
      background:#d9d2dd; position:relative; cursor:pointer; padding:0;
      -webkit-tap-highlight-color:transparent; transition:background 0.15s;
    }
    .poi-switch span {
      position:absolute; top:3px; left:3px; width:18px; height:18px; border-radius:50%;
      background:#fff; transition:transform 0.15s; box-shadow:0 1px 3px rgba(0,0,0,0.25);
    }
    .poi-switch-on { background:#8B008B; }
    .poi-switch-on span { transform:translateX(18px); }
    .poi-switch-partial { background:#c79bc7; }
    .poi-switch-partial span { transform:translateX(9px); }

    /* ── individual kind chips ── */
    .poi-panel-chips {
      display:flex; flex-wrap:wrap; gap:7px; padding:0 13px 13px;
    }
    .poi-chip-icon { display:inline-block; vertical-align:-1px; }
    .poi-chip-img { width:15px; height:15px; }
    .poi-panel-chip {
      max-width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
      padding:7px 12px; border-radius:999px; font-size:0.84rem; font-weight:600;
      border:1px solid rgba(139,0,139,0.3); background:rgba(255,255,255,0.85);
      color:#1c1020; cursor:pointer; transition:background 0.12s, color 0.12s, border-color 0.12s;
      -webkit-tap-highlight-color:transparent;
    }
    .poi-panel-chip.on { background:linear-gradient(150deg,#8B008B,#a8259c); color:#fff; border-color:transparent; }
    .poi-panel-chip:not(.on) { opacity:0.7; }
  `;
  const el = document.createElement('style');
  el.textContent = css;
  document.head.appendChild(el);
}
